import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Select,
  Button,
  Card,
  Divider,
  Pagination,
  Spin,
} from "antd";
import {
  FilterOutlined,
  DownOutlined,
  UserOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import LeadCard from "./LeadCard";
import AdminCard from "./AdminCard"; // Acts as Priority card logic
import PriorityLeadCard from "./PriorityLeadCard";
import StatisticsChart from "./StatisticsChart";
import useBeforeLoginApi from "../../hooks/useBeforeLoginApi";
import axiosPublic from "../../store/api/axiosPublic";
import Header from "../../components/Header";
import { formatAmountUAE } from "./FormatAmount";
import { useClientPagination } from "../../hooks/useClientPagination";
import { NoLeads } from "../../utils/paginate";

const { Title, Text } = Typography;

const BeforeLoginMobile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userName = localStorage.getItem("name");

  // States from your Web Logic
  const [selectedLead, setSelectedLead] = useState(null);
  // const [statusFilter, setStatusFilter] = useState("apartment");
  const [subcategoryLeadsMap, setSubcategoryLeadsMap] = useState({});
  const [subcategoryLoading, setSubcategoryLoading] = useState({});
  const [lastSearchParams, setLastSearchParams] = useState({});

  const [locations, setLocations] = useState([]);
  const [landTypes, setLandTypes] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [filters, setFilters] = useState({
    location: [],
    landType: [],
    budget: [],
  });
  const [filteredLeadsNav, setFilteredLeadsNav] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");

  // API Hook (Web logic)
  const {
    allLeads,
    pagination,
    cartLeads,
    loading,
    fetchAllLeads,
    addLeadToCart,
    removeLeadFromCart,
  } = useBeforeLoginApi();

  // Data Transformation Logic
  const transformLeadData = (rawLead) => ({
    id: rawLead._id,
    name: rawLead.name,
    place: rawLead.location,
    price: rawLead.budget,
    currentRate: rawLead.currentRate,
    landType: rawLead.subcategory?.label || "",
    rating: `${rawLead.timesBought || 0}/${rawLead.leadAvailableCount || 0}`,
    status: rawLead.status || "",
    siteVisit: rawLead.siteVisitDays?.join(", ") || "",
    lookingFor:
      rawLead.property_details || rawLead.category?.label || "No details",
    contact: rawLead.phone,
    type: rawLead.leadType,
    sqft: rawLead.spaceSqft || 0,
    subCategoryLabel: rawLead.subcategory?.label || "",
    subCategoryName: rawLead.subcategory?.name || "",
    state: rawLead.state || "",
    district: rawLead.district || "",
    area: rawLead.area || "",
    pincode: rawLead.pincode || "",
  });

  // Fetch Filter Options
  useEffect(() => {
    const fetchOptions = async (field, setter) => {
      try {
        const res = await axiosPublic.get("admin/publicleads/common", {
          params: { field },
        });
        setter(
          (res.data?.data || []).map((item) => ({
            value: item.value,
            label:
              typeof item.value === "number"
                ? item.value.toLocaleString("en-IN")
                : item.value,
          })),
        );
      } catch (err) {
        console.error(err);
      }
    };
    fetchOptions("location", setLocations);
    fetchOptions("landType", setLandTypes);
    fetchOptions("budget", setBudgets);
  }, []);

  // Sync leads
  const finalLeads = useMemo(() => {
    const source = filteredLeadsNav.length > 0 ? filteredLeadsNav : allLeads;
    return Array.isArray(source) ? source.map(transformLeadData) : [];
  }, [allLeads, filteredLeadsNav]);

  // Priority Lead Logic
  const priorityLead = useMemo(() => {
    return finalLeads.find((l) => {
      const [bought, total] = l.rating.split("/").map(Number);
      return total - bought === 1;
    });
  }, [finalLeads]);

  const subCategoryOptions = [
    { label: "Plot", value: "plot" },
    { label: "Villa", value: "villa" },
    { label: "Apartment", value: "apartment" },
    { label: "Office", value: "office" },
    { label: "Shop", value: "shop" },
  ];

  // const getSelectedLeadIdFromURL = () => {
  //   const params = new URLSearchParams(location.search);
  //   return params.get("selectedleadid"); // returns the string ID or null
  // };

  const selectedLeadIdFromURL = new URLSearchParams(location.search).get(
    "selectedleadid",
  );

  // const displayedLeads = useMemo(() => {
  //   if (activeCategory === "all") return finalLeads;
  //   return subcategoryLeadsMap[activeCategory] || [];
  // }, [activeCategory, finalLeads, subcategoryLeadsMap]);

  useEffect(() => {
    if (!selectedLeadIdFromURL) return;

    fetchAllLeads(1, {
      ...lastSearchParams,
      leadId: selectedLeadIdFromURL,
    });
  }, [selectedLeadIdFromURL]);

  useEffect(() => {
    if (!selectedLeadIdFromURL || !allLeads.length) return;

    const foundLead = allLeads.find(
      (lead) => lead._id === selectedLeadIdFromURL,
    );

    if (foundLead) {
      setSelectedLead(transformLeadData(foundLead));
    }
  }, [allLeads, selectedLeadIdFromURL]);

  // Handlers
  const handleSearch = () => {
    const params = {};
    if (filters.location?.length) params.location = filters.location.join(",");
    if (filters.landType?.length) params.landType = filters.landType.join(",");
    if (filters.budget?.length) {
      params.budget = filters.budget.join(",");
    }

    if (activeCategory !== "all") {
      setActiveCategory("all");
    }

    setLastSearchParams(params);
    fetchAllLeads(1, params);
  };

  const handleClear = () => {
    setFilters({ location: [], landType: [], budget: [] });
    setLastSearchParams({});
    setFilteredLeadsNav([]);
    fetchAllLeads(1, {});
    navigate(location.pathname, { replace: true, state: {} });
  };

  const fetchLeadsBySubcategory = async (subcat) => {
    setSubcategoryLoading((p) => ({ ...p, [subcat]: true }));
    try {
      const res = await axiosPublic.get(
        `admin/publicleads/subcategory/${subcat}`,
      );
      setSubcategoryLeadsMap((prev) => ({
        ...prev,
        [subcat]: (res.data?.data || []).map(transformLeadData),
      }));
    } finally {
      setSubcategoryLoading((p) => ({ ...p, [subcat]: false }));
    }
  };

  useEffect(() => {
    subCategoryOptions.forEach((sc) => {
      fetchLeadsBySubcategory(sc.value);
    });
  }, []);

  const CATEGORY_PAGE_SIZE = 10;

  const isAllTab = activeCategory === "all";

  const categoryPagination = useClientPagination({
    data: isAllTab ? [] : subcategoryLeadsMap[activeCategory] || [],
    pageSize: CATEGORY_PAGE_SIZE,
    resetKey: activeCategory,
  });

  const displayedLeads = isAllTab ? finalLeads : categoryPagination.items;

  return (
    <div>
      <Header />
      <div
        style={{ padding: "16px", paddingBottom: 100, background: "#f9f9f9" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 12px",
              background: "#fff",
              borderRadius: 8,
              border: "1px solid #f0f0f0",
            }}
          >
            <UserOutlined style={{ color: "#1890ff" }} />
            <Text strong>user</Text>
          </div>
        </div>

        {/* 2. SEARCH SECTION */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Search Lead
          </Title>
          <FilterOutlined
            style={{ fontSize: 20, color: "#1890ff" }}
            onClick={() => navigate("/sign-up")}
          />
        </div>

        <Card
          bordered
          style={{ borderColor: "#98c3ff", marginBottom: 12, borderRadius: 12 }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1px 1fr" }}>
            <Select
              mode="multiple"
              placeholder="Location"
              bordered={false}
              value={filters.location}
              options={locations}
              onChange={(v) => setFilters({ ...filters, location: v })}
            />
            <div style={{ width: 1, background: "#f0f0f0" }} />
            <Select
              mode="multiple"
              placeholder="Land Type"
              bordered={false}
              value={filters.landType}
              options={landTypes}
              onChange={(v) => setFilters({ ...filters, landType: v })}
            />
          </div>
          <Divider style={{ margin: "8px 0" }} />
          <Select
            mode="multiple"
            placeholder="Budget Range"
            bordered={false}
            style={{ width: "100%" }}
            value={filters.budget}
            options={budgets}
            onChange={(v) => setFilters({ ...filters, budget: v })}
          />
        </Card>

        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          <Button type="primary" block onClick={handleSearch}>
            Search
          </Button>
          <Button danger onClick={handleClear}>
            Clear
          </Button>
        </div>

        {/* 3. PRIORITY CARD */}
        {priorityLead && (
          <PriorityLeadCard
            name={priorityLead.name}
            place={priorityLead.place}
            rating={priorityLead.rating}
            onTopPickClick={(leadId) =>
              navigate(`/retail-leads?selectedleadid=${leadId}`)
            }
          />
        )}

        {/* 4. PAGINATED LEADS */}

        <div style={{ marginBottom: 16 }}>
          {/* Title */}
          <Title level={4} style={{ margin: 0 }}>
            Leads
          </Title>

          {/* Space ABOVE scroll */}
          <div style={{ height: 14 }} />

          {/* Scrollable Tabs */}
          <div
            style={{
              display: "flex",
              gap: 6,
              overflowX: "auto",
              paddingBottom: 6,
            }}
          >
            {[{ label: "All", value: "all" }, ...subCategoryOptions].map(
              (cat) => (
                <Button
                  key={cat.value}
                  type={activeCategory === cat.value ? "primary" : "default"}
                  onClick={() => setActiveCategory(cat.value)}
                  style={{
                    flexShrink: 0,
                    borderRadius: 20,
                    padding: "10px 18px", // slightly taller tabs
                    minHeight: 40, // improves tap area
                    marginBottom: 4,
                  }}
                >
                  {cat.label}
                </Button>
              ),
            )}
          </div>

          {/* Space BELOW scroll */}
          <div style={{ height: 12 }} />
        </div>
        {activeCategory === "all" &&
          Object.keys(lastSearchParams).length > 0 && (
            <Text
              type="secondary"
              style={{
                fontSize: 12,
                marginBottom: 8,
                display: "block",
              }}
            >
              {filters.location.length ||
              filters.landType.length ||
              filters.budget.length
                ? "Search results from all leads"
                : "Filtered results from all leads"}
            </Text>
          )}
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            padding: "8px",
            marginBottom: "4px",
          }}
        >
          {loading ? (
            <div style={{ textAlign: "center", padding: 20 }}>
              <Spin />
            </div>
          ) : displayedLeads.length === 0 ? (
            <NoLeads
              text={
                activeCategory === "all"
                  ? "No leads available"
                  : `No ${activeCategory} leads available`
              }
            />
          ) : (
            displayedLeads.map((lead) => (
              <div key={lead.id} style={{ marginBottom: 8 }}>
                <LeadCard
                  {...lead}
                  showCheckbox
                  checked={cartLeads.some((l) => l.id === lead.id)}
                  onCheck={(id, checked) =>
                    checked ? addLeadToCart(lead) : removeLeadFromCart(id)
                  }
                  onClick={() =>
                    setSelectedLead(selectedLead?.id === lead.id ? null : lead)
                  }
                />

                {/* INLINE DETAILS */}
                {selectedLead?.id === lead.id && (
                  <Card
                    style={{
                      background: "#fafafa",
                      marginTop: 8,
                      border: "1px solid #DBEAFF",
                    }}
                  >
                    <Text strong>Lead Details</Text>
                    <Divider style={{ margin: "8px 0" }} />
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1px 1fr",
                        gap: "10px",
                      }}
                    >
                      <div>
                        <Text type="secondary">Name</Text>
                        <div>{lead.name}</div>
                      </div>
                      <div style={{ width: 1, background: "#DBEAFF" }} />
                      <div>
                        <Text type="secondary">Contact No.</Text>
                        <div>{lead.contact}</div>
                      </div>

                      <div>
                        <Text type="secondary">Client Location</Text>
                        <div>{lead.place}</div>
                      </div>
                      <div style={{ width: 1, background: "#DBEAFF" }} />
                      <div>
                        <Text type="secondary">Sq.ft</Text>
                        <div>{lead.sqft}</div>
                      </div>

                      <div>
                        <Text type="secondary">Budget</Text>
                        <div>
                          {formatAmountUAE(lead.price)}{" "}
                          <span className="text-xs text-gray-500 ml-1">
                            AED
                          </span>
                        </div>
                      </div>
                      <div style={{ width: 1, background: "#DBEAFF" }} />
                      <div>
                        <Text type="secondary">Preferred Location</Text>
                        <div>
                          {lead?.state || ""}-{lead?.district || ""}-
                          {lead?.area || ""}--
                          {lead?.pincode || ""}
                        </div>
                      </div>

                      <div>
                        <Text type="secondary">Site Visit</Text>
                        <div>{lead.siteVisit}</div>
                      </div>
                      <div style={{ width: 1, background: "#DBEAFF" }} />
                      <div>
                        <Text type="secondary">Looking For</Text>
                        <div>{lead.lookingFor}</div>
                      </div>
                    </div>
                    <Button
                      type="primary"
                      block
                      style={{ marginTop: 16 }}
                      onClick={() => addLeadToCart(lead, true)}
                    >
                      Buy Now
                    </Button>
                  </Card>
                )}
              </div>
            ))
          )}
        </div>

        {isAllTab ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: 16,
            }}
          >
            <Pagination
              simple
              current={pagination.page}
              total={pagination.total || 10}
              pageSize={pagination.limit}
              showSizeChanger={false}
              onChange={(page) => fetchAllLeads(page, lastSearchParams)}
              style={{ marginTop: 8, textAlign: "center" }}
            />
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: 16,
            }}
          >
            <Pagination
              simple
              current={categoryPagination.page}
              total={categoryPagination.total || 10}
              pageSize={CATEGORY_PAGE_SIZE}
              showSizeChanger={false}
              onChange={categoryPagination.setPage}
            />
          </div>
        )}
        {/* 5. CHART */}
        <Title level={5} style={{ marginTop: 24 }}>
          Lead Statistics
        </Title>
        <Card style={{ borderRadius: 12 }}>
          <StatisticsChart />
        </Card>

        {/* 6. CATEGORY FILTER & BOTTOM LEADS */}
        {/* <div style={{ marginTop: 24 }}>
          <Title level={5}>Leads by Category</Title>
          <Select
            value={statusFilter}
            suffixIcon={<DownOutlined />}
            onChange={setStatusFilter}
            options={subCategoryOptions}
            style={{ width: "100%", marginBottom: 12 }}
          />
          <Card style={{ borderRadius: 12 }}>
            {subcategoryLoading[statusFilter] ? (
              <Spin />
            ) : (
              subcategoryLeadsMap[statusFilter]?.map((lead) => (
                <LeadCard
                  key={lead.id}
                  {...lead}
                  showCheckbox={false}
                  onClick={() => setSelectedLead(lead)}
                />
              ))
            )}
          </Card>
        </div> */}

        {/* 7. CART BAR */}
        {cartLeads.length > 0 && (
          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              background: "#fff",
              padding: "16px",
              borderTop: "2px solid #1890ff",
              display: "flex",
              justifyContent: "space-between",
              zIndex: 1000,
              boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
            }}
          >
            <div>
              <strong>{cartLeads.length} leads</strong> selected
              <div style={{ fontSize: 11, color: "red" }}>
                Login required for purchase
              </div>
            </div>
            <Button
              type="primary"
              icon={<ShoppingCartOutlined />}
              onClick={() => navigate("/sign-up")}
            >
              Add to Cart
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BeforeLoginMobile;
